import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router"
import AppLayout from "../../components/layout/AppLayout"
import CalendarView from "../../components/post-schedule/CalendarView"
import { api } from "../../lib/api"
import { ScheduledPost, PostType } from "../../types/scheduled-post"
import { useSelectedAccount } from "../../contexts/AccountContext"
import PlanLockNotice from "../../components/account/PlanLockNotice"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import { PAGE_TITLES } from "../../lib/navItems"
import { getAccountColor } from "../../utils/accountColors"
import "../../styles/post-schedule.scss"

const toYearMonth = (date: Date): { year: number; month: number } => ({
  year:  date.getFullYear(),
  month: date.getMonth() + 1,
})

const formatYearMonth = (year: number, month: number): string =>
  `${year}年${month}月`

const toMonthParam = (year: number, month: number): string =>
  `${year}-${String(month).padStart(2, "0")}`

const formatUpcomingDate = (iso: string): string => {
  const d = new Date(iso)
  return `${d.getMonth() + 1}月${d.getDate()}日 ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`
}

const POST_TYPE_ICONS: Record<PostType, string> = {
  feed:     "bi-image",
  story:    "bi-circle",
  reel:     "bi-play-btn",
  carousel: "bi-images",
}

const POST_TYPE_LABELS: Record<PostType, string> = {
  feed:     "フィード",
  story:    "ストーリーズ",
  reel:     "リール",
  carousel: "カルーセル",
}

const PostSchedulePage = () => {
  const navigate = useNavigate()
  const today = new Date()
  const [current, setCurrent] = useState(toYearMonth(today))
  const [posts, setPosts] = useState<ScheduledPost[]>([])
  const [loading, setLoading] = useState(true)
  const [currentMonthPosts, setCurrentMonthPosts] = useState<ScheduledPost[]>([])
  const [draftPosts, setDraftPosts] = useState<ScheduledPost[]>([])

  const { selectedAccountIds, selectedOrganizationId, organizations, currentPermission, selectedAccount } = useSelectedAccount()
  const canEdit = currentPermission === "edit"
  const allAccounts = organizations.flatMap(o => o.accounts)
  const scheduledPostLocked = selectedAccount != null && !selectedAccount.features.scheduled_post

  const [publishingId, setPublishingId] = useState<number | null>(null)
  const [publishNowError, setPublishNowError] = useState<string | null>(null)

  const handlePublishNow = async (post: ScheduledPost, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!window.confirm("今すぐInstagramに投稿します。よろしいですか？")) return
    setPublishingId(post.id)
    setPublishNowError(null)
    try {
      const params = selectedOrganizationId != null ? `?organization_id=${selectedOrganizationId}` : ""
      await api.patch(`/scheduled_posts/${post.id}${params}`, { scheduled_post: { status: "publishing" } })
      setDraftPosts(prev => prev.filter(p => p.id !== post.id))
      fetchPosts(current.year, current.month)
    } catch (err: unknown) {
      const error = err as { data?: { error?: string } }
      setPublishNowError(error?.data?.error ?? "投稿に失敗しました")
    } finally {
      setPublishingId(null)
    }
  }

  const fetchPosts = useCallback(async (year: number, month: number) => {
    if (selectedAccountIds.length === 0) {
      setPosts([])
      setLoading(false)
      return
    }
    try {
      const params = new URLSearchParams({ month: toMonthParam(year, month) })
      selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
      if (selectedOrganizationId != null) params.set("organization_id", String(selectedOrganizationId))
      const data = await api.get<ScheduledPost[]>(`/scheduled_posts?${params}`)
      setPosts(data)
    } catch {
      // エラーは無視（既存データを保持）
    } finally {
      setLoading(false)
    }
  }, [selectedAccountIds, selectedOrganizationId])

  // 初回・月変更・アカウント変更時にフェッチ
  useEffect(() => {
    setLoading(true)
    fetchPosts(current.year, current.month)
  }, [current.year, current.month, fetchPosts])

  // publishingのポーリング
  useEffect(() => {
    const hasPublishing = posts.some(p => p.status === "publishing")
    if (!hasPublishing) return

    const timer = setInterval(() => {
      fetchPosts(current.year, current.month)
    }, 5000)

    return () => clearInterval(timer)
  }, [posts, current, fetchPosts])

  // サイドパネル用: 当月固定フェッチ（アカウント変更時のみ再取得）
  useEffect(() => {
    if (selectedAccountIds.length === 0) return
    const now = new Date()
    const params = new URLSearchParams({ month: toMonthParam(now.getFullYear(), now.getMonth() + 1) })
    selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
    if (selectedOrganizationId != null) params.set("organization_id", String(selectedOrganizationId))
    api.get<ScheduledPost[]>(`/scheduled_posts?${params}`).then(setCurrentMonthPosts).catch(() => {})
  }, [selectedAccountIds, selectedOrganizationId])

  // 下書き一覧フェッチ（アカウント変更時のみ再取得）
  useEffect(() => {
    if (selectedAccountIds.length === 0) { setDraftPosts([]); return }
    const params = new URLSearchParams({ status: "draft" })
    selectedAccountIds.forEach(id => params.append("instagram_account_ids[]", String(id)))
    if (selectedOrganizationId != null) params.set("organization_id", String(selectedOrganizationId))
    api.get<ScheduledPost[]>(`/scheduled_posts?${params}`).then(setDraftPosts).catch(() => {})
  }, [selectedAccountIds, selectedOrganizationId])

  // サマリー集計（draft は除外）
  const nonDraftPosts = currentMonthPosts.filter(p => p.status !== "draft")
  const scheduledCount = nonDraftPosts.filter(p => p.status === "scheduled").length
  const publishedCount = nonDraftPosts.filter(p => p.status === "published").length
  const failedCount    = nonDraftPosts.filter(p => p.status === "failed").length
  const total = nonDraftPosts.length

  // 直近の予約（当月内・今以降・昇順5件）
  const nowStr = new Date().toISOString()
  const upcoming = currentMonthPosts
    .filter(p => p.status === "scheduled" && p.scheduled_at && p.scheduled_at > nowStr)
    .sort((a, b) => (a.scheduled_at ?? "").localeCompare(b.scheduled_at ?? ""))
    .slice(0, 5)

  const goToPrevMonth = () => {
    setCurrent(prev => {
      const d = new Date(prev.year, prev.month - 2, 1)
      return toYearMonth(d)
    })
  }

  const goToNextMonth = () => {
    setCurrent(prev => {
      const d = new Date(prev.year, prev.month, 1)
      return toYearMonth(d)
    })
  }

  const goToToday = () => setCurrent(toYearMonth(new Date()))

  return (
    <AppLayout pageTitle={PAGE_TITLES["/post-schedule"]}>
      <div className="post-schedule">
        {selectedAccountIds.length === 0 ? (
          <AccountSelectionNotice />
        ) : scheduledPostLocked ? (
          <PlanLockNotice message={`${PAGE_TITLES["/post-schedule"]}はプロプラン以上でご利用いただけます`} />
        ) : (
          <>
            {/* ヘッダー */}
            <div className="post-schedule__header">
              <div className="post-schedule__nav">
                <button className="ps-btn ps-btn--icon" onClick={goToPrevMonth} aria-label="前の月">
                  ‹
                </button>
                <span className="post-schedule__month-title">
                  {formatYearMonth(current.year, current.month)}
                </span>
                <button className="ps-btn ps-btn--icon" onClick={goToNextMonth} aria-label="次の月">
                  ›
                </button>
              </div>

              <div className="post-schedule__actions">
                <button className="ps-btn" onClick={goToToday}>今日</button>
                {selectedAccountIds.length === 1 && canEdit && (
                  <button
                    className="ps-btn ps-btn--primary"
                    onClick={() => navigate("/post-schedule/new")}
                  >
                    + 新規予約
                  </button>
                )}
              </div>
            </div>

            {/* カレンダー＋サイドパネル */}
            <div className="post-schedule__layout">
              <div className="post-schedule__main">
                {loading ? (
                  <div style={{ textAlign: "center", padding: 40, color: "#9ca3af" }}>
                    <span className="spinner" style={{ borderColor: "#e5e7eb", borderTopColor: "#6366f1", width: 24, height: 24 }} />
                  </div>
                ) : (
                  <CalendarView
                    year={current.year}
                    month={current.month}
                    posts={posts}
                    allAccounts={allAccounts}
                    selectedAccountIds={selectedAccountIds}
                  />
                )}
              </div>

              {/* サイドパネル */}
              <div className="post-schedule__sidebar">
                {/* 今月のサマリー */}
                <div className="ps-card">
                  <div className="ps-card__title">今月のサマリー</div>
                  <div className="ps-summary__rows">
                    <div className="ps-summary__row">
                      <span className="ps-summary__dot" style={{ background: "#FF9100" }} />
                      <span className="ps-summary__label">予約済み</span>
                      <span className="ps-summary__count">{scheduledCount}</span>
                    </div>
                    <div className="ps-summary__row">
                      <span className="ps-summary__dot" style={{ background: "#22c55e" }} />
                      <span className="ps-summary__label">公開済み</span>
                      <span className="ps-summary__count">{publishedCount}</span>
                    </div>
                    <div className="ps-summary__row">
                      <span className="ps-summary__dot" style={{ background: "#ef4444" }} />
                      <span className="ps-summary__label">失敗</span>
                      <span className="ps-summary__count">{failedCount}</span>
                    </div>
                  </div>
                  <div className="ps-summary__bar">
                    {total > 0 ? (
                      <>
                        <div className="ps-summary__bar-segment" style={{ width: `${(scheduledCount / total) * 100}%`, background: "#FF9100" }} />
                        <div className="ps-summary__bar-segment" style={{ width: `${(publishedCount / total) * 100}%`, background: "#22c55e" }} />
                        <div className="ps-summary__bar-segment" style={{ width: `${(failedCount / total) * 100}%`, background: "#ef4444" }} />
                      </>
                    ) : (
                      <div className="ps-summary__bar-segment" style={{ width: "100%", background: "#e5e7eb" }} />
                    )}
                  </div>
                </div>

                {/* 直近の予約 */}
                <div className="ps-card">
                  <div className="ps-card__title">直近の予約</div>
                  {upcoming.length === 0 ? (
                    <p className="ps-upcoming__empty">今月の予約投稿はまだありません</p>
                  ) : (
                    <div className="ps-upcoming__list">
                      {upcoming.map(post => {
                        const isVideo = post.media_attachments[0]?.content_type.startsWith("video/") ?? false
                        const color   = getAccountColor(post.instagram_account.id, allAccounts)
                        return (
                          <div key={post.id} className={`ps-upcoming__item${canEdit ? " ps-upcoming__item--clickable" : ""}`} onClick={canEdit ? () => navigate(`/post-schedule/${post.id}`) : undefined}>
                            <div className="ps-upcoming__thumb-wrap">
                              {post.media_attachments[0]?.url ? (
                                isVideo ? (
                                  <video className="ps-upcoming__thumb" src={post.media_attachments[0].url} preload="metadata" muted />
                                ) : (
                                  <img className="ps-upcoming__thumb" src={post.media_attachments[0].url} alt="" />
                                )
                              ) : (
                                <i className={`bi ${POST_TYPE_ICONS[post.post_type]}`} style={{ fontSize: 18, color: "#9ca3af" }} />
                              )}
                            </div>
                            <div className="ps-upcoming__info">
                              <div className="ps-upcoming__date">
                                <span className="ps-upcoming__badge" style={{ background: color }} />
                                {post.scheduled_at ? formatUpcomingDate(post.scheduled_at) : "—"}
                              </div>
                              <div className="ps-upcoming__username">@{post.instagram_account.username}</div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 下書き一覧 */}
            {draftPosts.length > 0 && (
              <div className="ps-drafts">
                <div className="ps-drafts__header">
                  <span className="ps-drafts__title">下書き</span>
                  <span className="ps-drafts__count">{draftPosts.length}件</span>
                </div>
                {publishNowError && (
                  <div className="media-errors__item" style={{ marginBottom: 12 }}>{publishNowError}</div>
                )}
                <div className="ps-drafts__grid">
                  {draftPosts.map(post => (
                    <div key={post.id} className={`ps-draft-card${canEdit ? "" : " ps-draft-card--readonly"}`} onClick={canEdit ? () => navigate(`/post-schedule/${post.id}`) : undefined}>
                      <div className="ps-draft-card__thumb-wrap">
                        {post.media_attachments[0]?.url
                          ? post.media_attachments[0].content_type.startsWith("video/")
                            ? <video className="ps-draft-card__thumb" src={post.media_attachments[0].url} preload="metadata" muted />
                            : <img className="ps-draft-card__thumb" src={post.media_attachments[0].url} alt="" />
                          : <i className={`bi ${POST_TYPE_ICONS[post.post_type]}`} style={{ fontSize: 24, color: "#9ca3af" }} />
                        }
                      </div>
                      <div className="ps-draft-card__body">
                        <div className="ps-draft-card__title">{post.title ?? "タイトルなし"}</div>
                        <div className="ps-draft-card__meta">
                          <span className="ps-draft-card__account">@{post.instagram_account.username}</span>
                          <span className="ps-draft-card__type">{POST_TYPE_LABELS[post.post_type]}</span>
                        </div>
                        {post.caption && (
                          <div className="ps-draft-card__caption">
                            {post.caption.slice(0, 60)}{post.caption.length > 60 ? "…" : ""}
                          </div>
                        )}
                        {canEdit && (
                          <div className="ps-draft-card__footer">
                            <button
                              className="ps-btn ps-draft-card__publish-btn"
                              onClick={(e) => handlePublishNow(post, e)}
                              disabled={publishingId === post.id}
                            >
                              <i className="bi bi-send" />
                              {publishingId === post.id ? "投稿中..." : "今すぐ投稿"}
                            </button>
                          </div>
                        )}
                      </div>
                      <i className="bi bi-pencil-square ps-draft-card__edit-icon" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default PostSchedulePage
